// The ONE shared keyset infinite-list hook (UI Layering Sweep Phase 2). Every
// `/v1` list read follows the same shape — `page_size`/`page_token` keyset
// pagination (docs/04 §9, docs/09 §6) through the generated client, throwing
// `ApiRequestError` on the Status envelope, flattening pages into `items` —
// so the `useInfiniteQuery` boilerplate lives here exactly once. Query keys
// are ALWAYS passed in by the caller from the central `queryKeys` factory
// (filters ride inside the key); this hook never invents or reshapes keys.

import { hashKey, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import type { paths } from "../api/schema";
import { apiClient } from "./apiClient";
import {
  ApiRequestError,
  requestErrorDescription,
  type ApiError,
} from "./errors";
import {
  DEFAULT_PAGE_SIZE,
  nextPageParam,
  type KeysetPage,
} from "./pagination";

const firstPageToken: string | undefined = undefined;

type PathGetOperation<Path extends keyof paths> = paths[Path] extends {
  get: infer Operation;
}
  ? Operation
  : never;

type GetSuccessJson<Operation> = Operation extends {
  responses: { 200: { content: { "application/json": infer Body } } };
}
  ? Body
  : never;

// Every GET path whose 200 body is a keyset page (`{ items, next_page_token }`)
// — the only paths this hook accepts.
export type KeysetListPath = {
  [Path in keyof paths]: GetSuccessJson<PathGetOperation<Path>> extends {
    items: unknown[];
    next_page_token: string | null;
  }
    ? Path
    : never;
}[keyof paths];

// The row type a given list path yields, inferred from the generated schema.
export type ListQueryItem<Path extends KeysetListPath> =
  GetSuccessJson<PathGetOperation<Path>> extends { items: Array<infer Item> }
    ? Item
    : never;

type OperationQuery<Operation> = Operation extends {
  parameters: { query?: infer Query };
}
  ? NonNullable<Query>
  : never;

type OperationPath<Operation> = Operation extends {
  parameters: { path?: infer Path };
}
  ? NonNullable<Path>
  : never;

// The path's own query filters (e.g. `agent_id`), minus the pagination params
// this hook owns. `undefined` values are omitted from the wire by the client's
// query serializer, so conditional filters pass `undefined` to mean "absent".
export type ListQueryFilters<Path extends KeysetListPath> = Omit<
  OperationQuery<PathGetOperation<Path>>,
  "page_size" | "page_token"
>;

export type ListQueryPathParams<Path extends KeysetListPath> = OperationPath<
  PathGetOperation<Path>
>;

export interface ListQueryOptions<Path extends KeysetListPath> {
  // Defer fetching (e.g. a drawer-only list). Default true.
  enabled?: boolean;
  // Page size override; defaults to DEFAULT_PAGE_SIZE (50, server cap 200).
  pageSize?: number;
  // Optional bounded refresh cadence for server state that changes without an
  // SSE invalidation (for example schedule fire state).
  refetchInterval?: number | false;
  // Freshness override for reads that must be revalidated when enabled.
  staleTime?: number;
  // Path-specific query filters. MUST also ride inside `queryKey` (the
  // central factory carries filters in the key) so a filter change is a
  // fresh keyset stream, never an offset synthesis.
  params?: ListQueryFilters<Path>;
  // Nested list routes (for example marker revision history) bind their
  // parent id here. Top-level list routes omit this field.
  pathParams?: ListQueryPathParams<Path>;
  // From the central `queryKeys` factory, verbatim — e.g.
  // `queryKeys.apiKeys.list({ agent_id })`. Never built inline.
  queryKey: readonly unknown[];
}

export interface ListQueryResult<Item> {
  errorMessage: string | undefined;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetching?: boolean;
  isFetchingNextPage: boolean;
  isPending: boolean;
  items: Item[];
  // Stable identity for this list's cache entry — TanStack's own key hash of
  // `options.queryKey`, so it changes exactly when the list (route, scope,
  // filters) changes. Per-list view state keys off this, never off a
  // hand-rolled hash.
  listStateKey: string;
}

// The page cached by TanStack is the generated response shape itself. Keep
// list consumers on this alias rather than re-declaring `{ items: T[] }`.
export type ListQueryPage<Path extends KeysetListPath> = GetSuccessJson<
  PathGetOperation<Path>
>;

type ListPageOf<Path extends KeysetListPath> = ListQueryPage<Path> &
  KeysetPage & {
    items: Array<ListQueryItem<Path>>;
  };

// openapi-fetch cannot type-check a call whose path is itself a generic type
// parameter; this alias narrows GET to the keyset-list contract that the
// `KeysetListPath` constraint already guarantees. The hook's public inputs
// and outputs stay fully typed against the generated schema.
const listGet = apiClient.GET as (
  path: KeysetListPath,
  init: {
    params: {
      path?: Record<string, unknown>;
      query: Record<string, unknown>;
    };
  },
) => Promise<{ data?: unknown; error?: ApiError }>;

export function useListQuery<Path extends KeysetListPath>(
  path: Path,
  options: ListQueryOptions<Path>,
): ListQueryResult<ListQueryItem<Path>> {
  const query = useInfiniteQuery({
    enabled: options.enabled,
    // A list keeps its loaded pages while the user is off in a detail route:
    // without a gc window longer than the visit, returning to the list drops
    // every page but the first and re-walks the keyset from the top.
    // ASSUMED(docs/09 does not state per-list cache windows; 30 min retention
    // with a 60s freshness window covers a detail round-trip without letting a
    // list go visibly stale.)
    gcTime: 30 * 60 * 1000,
    staleTime: options.staleTime ?? 60_000,
    queryKey: options.queryKey,
    initialPageParam: firstPageToken,
    queryFn: async ({ pageParam }): Promise<ListPageOf<Path>> => {
      const { data, error } = await listGet(path, {
        params: {
          path: options.pathParams,
          query: {
            ...options.params,
            page_size: options.pageSize ?? DEFAULT_PAGE_SIZE,
            page_token: pageParam,
          },
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      if (!data) {
        throw new Error("Empty API response");
      }
      return data as ListPageOf<Path>;
    },
    getNextPageParam: nextPageParam,
    refetchInterval: options.refetchInterval,
  });

  // `pages` keeps its identity across renders (TanStack's structural sharing
  // replaces it only when a page actually changes), so the flattened rows keep
  // theirs. That identity is load-bearing: DataTable hands this array straight
  // to `useReactTable`, which uses `data` as a memo key — a fresh array on
  // every render rebuilds the core row model (O(n)) and re-runs sorting
  // (O(n log n)) on every unrelated parent re-render (a filter chip, a drawer,
  // a sibling query settling).
  const pages = query.data?.pages;
  const items = useMemo(
    () => pages?.flatMap((page) => page.items) ?? [],
    [pages],
  );

  return {
    errorMessage: query.isError
      ? requestErrorDescription(query.error)
      : undefined,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
    hasNextPage: query.hasNextPage,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isPending: query.isPending,
    items,
    listStateKey: hashKey(options.queryKey),
  };
}
